// Client-side image compression & EXIF stripping.
// Uses pure Canvas API — no external dependencies.
//
// The canvas re-encode automatically discards all EXIF metadata
// including GPS coordinates, which is what we want for privacy.

import i18n from "@/lib/i18n";

/**
 * Compresses an image file to a JPEG Blob.
 *
 * - Long side is scaled down to `maxDimension` (never upscaled).
 * - Short side is scaled proportionally.
 * - Always re-encodes to JPEG even when below maxDimension (to strip EXIF).
 *
 * Uses `createImageBitmap` when available — a streaming decoder that is
 * substantially more robust than `<img>` on mobile for large images
 * (e.g. Android collages that comfortably exceed the in-memory limit of
 * an HTMLImageElement but decode fine off-thread). Falls back to the
 * HTMLImageElement path for browsers without `createImageBitmap`.
 *
 * @param file           Source image File (any format browser can decode)
 * @param maxDimension   Max pixel length of the long side. Default 1600.
 * @param quality        JPEG quality 0–1. Default 0.85.
 */
export async function compressImage(
  file: File,
  maxDimension = 1600,
  quality = 0.85,
): Promise<Blob> {
  const source = await decodeImage(file);
  const { width: srcW, height: srcH } = sourceDimensions(source);

  const longSide = Math.max(srcW, srcH);
  const scale = longSide > maxDimension ? maxDimension / longSide : 1;
  const newWidth = Math.round(srcW * scale);
  const newHeight = Math.round(srcH * scale);

  const canvas = document.createElement("canvas");
  canvas.width = newWidth;
  canvas.height = newHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    releaseSource(source);
    throw decodeError();
  }

  ctx.drawImage(source as CanvasImageSource, 0, 0, newWidth, newHeight);
  releaseSource(source);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(decodeError())),
      "image/jpeg",
      quality,
    );
  });
}

/**
 * Same as compressImage but returns a File with the original name (extension replaced with .jpg).
 */
export async function compressImageToFile(
  file: File,
  maxDimension = 1600,
  quality = 0.85,
): Promise<File> {
  const blob = await compressImage(file, maxDimension, quality);
  const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

// ── Internals ────────────────────────────────────────────────────────────────

type DecodedSource = ImageBitmap | HTMLImageElement;

async function decodeImage(file: File): Promise<DecodedSource> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Fall through — some browsers reject specific MIME types here but
      // will still load them via <img>. Try the legacy path before giving up.
    }
  }
  return decodeViaImageElement(file);
}

function decodeViaImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(decodeError());
    };
    img.src = objectUrl;
  });
}

function sourceDimensions(source: DecodedSource): { width: number; height: number } {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  return { width: source.width, height: source.height };
}

function releaseSource(source: DecodedSource): void {
  if (source instanceof HTMLImageElement) {
    if (source.src.startsWith("blob:")) URL.revokeObjectURL(source.src);
    return;
  }
  // ImageBitmap — release GPU/CPU memory eagerly rather than waiting for GC.
  source.close();
}

function decodeError(): Error {
  return new Error(i18n.t("capture.photo.decodeFailed"));
}
